import React, { useEffect, useState, useRef } from "react";
import {
  FaWindows,
  FaApple,
  FaXbox,
  FaHeart,
  FaTimes,
  FaUser,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import {
  FiGrid,
  FiPlay,
  FiSearch,
  FiStar,
  FiZap,
  FiShoppingCart,
} from "react-icons/fi";
import { BiGitCompare } from "react-icons/bi";
import { DiAndroid } from "react-icons/di";
import { SiNintendoswitch, SiPlaystation, SiOculus, SiSteamdeck } from "react-icons/si";
import { TbDeviceVisionProFilled } from "react-icons/tb";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Modal } from "@mui/material";
import { BsGrid3X3Gap, BsListUl } from "react-icons/bs";
import { useLocation, useNavigate } from "react-router-dom";
import { getAllActiveGames } from "../Redux/Slice/game.slice";
import {
  addToCart as addToCartAction,
  fetchCart,
} from "../Redux/Slice/cart.slice";
import {
  addToWishlist,
  removeFromWishlist,
  fetchWishlist,
} from "../Redux/Slice/wishlist.slice";
import BackgroundColor from "../component/BackgroundColor";
import ProductSkeleton from "../component/skeleton-loaders/ProductSkeleton";
import SEOHead from "../component/SEOHead";

const PLATFORMS_CONFIG = [
  {
    id: "windows",
    name: "Windows PC",
    icon: FaWindows,
    colorClass: "text-blue-500",
    glowClass: "shadow-blue-500/20 hover:border-blue-500/50",
    gradient: "from-blue-600 to-cyan-500",
  },
  {
    id: "playstation",
    name: "PlayStation",
    icon: SiPlaystation,
    colorClass: "text-indigo-500",
    glowClass: "shadow-indigo-500/20 hover:border-indigo-500/50",
    gradient: "from-indigo-600 to-blue-500",
  },
  {
    id: "xbox",
    name: "Xbox",
    icon: FaXbox,
    colorClass: "text-green-500",
    glowClass: "shadow-green-500/20 hover:border-green-500/50",
    gradient: "from-green-600 to-emerald-500",
  },
  {
    id: "nintendo",
    name: "Nintendo Switch",
    icon: SiNintendoswitch,
    colorClass: "text-red-500",
    glowClass: "shadow-red-500/20 hover:border-red-500/50",
    gradient: "from-red-600 to-orange-500",
  },
  {
    id: "quest",
    name: "Meta Quest VR",
    icon: SiOculus,
    colorClass: "text-sky-400",
    glowClass: "shadow-sky-400/20 hover:border-sky-400/50",
    gradient: "from-sky-500 to-indigo-500",
  },
  {
    id: "visionpro",
    name: "Vision Pro",
    icon: TbDeviceVisionProFilled,
    colorClass: "text-purple-400",
    glowClass: "shadow-purple-400/20 hover:border-purple-400/50",
    gradient: "from-purple-600 to-pink-500",
  },
  {
    id: "ios",
    name: "iOS App Store",
    icon: FaApple,
    colorClass: "text-gray-200",
    glowClass: "shadow-gray-200/20 hover:border-gray-200/50",
    gradient: "from-gray-600 to-gray-400",
  },
  {
    id: "android",
    name: "Android Play Store",
    icon: DiAndroid,
    colorClass: "text-emerald-400",
    glowClass: "shadow-emerald-400/20 hover:border-emerald-400/50",
    gradient: "from-emerald-500 to-lime-400",
  },
  {
    id: "steamdeck",
    name: "Steam Deck",
    icon: SiSteamdeck,
    colorClass: "text-blue-400",
    glowClass: "shadow-blue-400/20 hover:border-blue-400/50",
    gradient: "from-blue-600 to-indigo-500",
  },
];

export default function Platforms() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedPlatform, setSelectedPlatform] = useState("windows");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [grid, setGrid] = useState(3);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const sortDropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const [compareGames, setCompareGames] = useState([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [dots, setDots] = useState([]);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [maxPageButtons, setMaxPageButtons] = useState(5);
  const gamesPerPage = 12;

  // Handle resize to adjust number of pagination buttons
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 600) {
        setMaxPageButtons(3);
      } else if (window.innerWidth < 1024) {
        setMaxPageButtons(4);
      } else {
        setMaxPageButtons(6);
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Reset page when platform, search or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedPlatform, searchTerm, sortBy]);

  // Redux state
  const gamesList = useSelector((state) => state.game.games || []);
  const isLoadingGames = useSelector(
    (state) => state.game.loadingStates.getAllActiveGames
  );
  const cartItems = useSelector((state) => state.cart?.items || []);
  const { wishlistStatus } = useSelector((state) => state.wishlist);
  const userId = localStorage.getItem("yoyouserId");

  useEffect(() => {
    document.title = "Platforms • YOYO";
    window.scrollTo(0, 0);
    dispatch(getAllActiveGames());

    const token = localStorage.getItem("yoyoToken");
    if (userId && token) {
      dispatch(fetchCart());
      dispatch(fetchWishlist());
    }
  }, [dispatch, userId]);

  // Handle outside click for sort dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSortOpen &&
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target)
      ) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSortOpen]);

  // Generate dropping animated dots
  useEffect(() => {
    const generateDots = () => {
      const newDots = [];
      const containerWidth = window.innerWidth;
      const containerHeight = window.innerHeight;
      for (let i = 0; i < 15; i++) {
        newDots.push({
          id: `drop-${i}`,
          x: Math.random() * containerWidth,
          startY: -20,
          delay: Math.random() * 5,
          duration: 3 + Math.random() * 3,
        });
      }
      setDots(newDots);
    };
    generateDots();
    window.addEventListener("resize", generateDots);
    return () => window.removeEventListener("resize", generateDots);
  }, []);

  const handleCardMouseEnter = (gameId) => {
    const videoEl = document.getElementById(`video-${gameId}`);
    if (videoEl) {
      videoEl.play().catch(() => { });
    }
  };

  const handleCardMouseLeave = (gameId) => {
    const videoEl = document.getElementById(`video-${gameId}`);
    if (videoEl) {
      videoEl.pause();
      videoEl.currentTime = 0;
    }
  };

  const isGameInCart = (gameId, platform) => {
    return cartItems.some(
      (item) =>
        String(item.game?._id || item.game) === String(gameId) &&
        item.platform === platform
    );
  };

  const toggleFavorite = (gameId) => {
    if (!userId) {
      navigate("/login");
      return;
    }
    const isInWishlist = wishlistStatus[gameId];
    if (isInWishlist) {
      dispatch(removeFromWishlist({ gameId }));
    } else {
      dispatch(addToWishlist({ gameId }));
    }
  };

  const handleToggleCompare = (game) => {
    if (compareGames.some((g) => g._id === game._id)) {
      setCompareGames((prev) => prev.filter((g) => g._id !== game._id));
    } else {
      if (compareGames.length >= 3) {
        alert("You can compare a maximum of 3 games.");
        return;
      }
      setCompareGames((prev) => [...prev, game]);
    }
  };

  // Filter games based on selected platform, search, and activity
  const filteredGames = gamesList.filter((game) => {
    const hasPlatform = game.platforms?.[selectedPlatform]?.available === true;
    const matchesSearch =
      !searchTerm ||
      game.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      game.tags?.some((tag) =>
        tag?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    return hasPlatform && matchesSearch;
  });

  // Sort games
  const sortedGames = [...filteredGames].sort((a, b) => {
    const priceA = a.platforms?.[selectedPlatform]?.price || 0;
    const priceB = b.platforms?.[selectedPlatform]?.price || 0;

    switch (sortBy) {
      case "price":
        return priceA - priceB;
      case "price_desc":
        return priceB - priceA;
      case "newest":
        return new Date(b.createdAt) - new Date(a.createdAt);
      default:
        return 0; // Maintain default API order for popular
    }
  });

  const totalPages = Math.ceil(sortedGames.length / gamesPerPage);
  const paginatedGames = sortedGames.slice(
    (currentPage - 1) * gamesPerPage,
    currentPage * gamesPerPage
  );

  const sortOptions = [
    { value: "popular", label: "Most Popular" },
    { value: "price", label: "Price: Low to High" },
    { value: "price_desc", label: "Price: High to Low" },
    { value: "newest", label: "Newest First" },
  ];

  return (
    <>
      <SEOHead
        title="Game Platforms"
        description="Browse games by platform on Yoyo Game. Find the best games for Windows PC, Android, iOS, PlayStation, Xbox, Nintendo Switch, Meta Quest VR, Steam Deck, and Apple Vision Pro."
        keywords="games by platform, PC games, Android games, iOS games, PlayStation games, Xbox games, Nintendo Switch games, Meta Quest VR games, Steam Deck games, Apple Vision Pro games"
        url="https://yoyokhel.com/platforms"
      />
    <BackgroundColor className="relative mx-auto w-[95%] sm:w-[92%] md:w-[90%] lg:max-w-[80%]">
      <div className="relative pt-24 pb-8 z-10">
        {/* Header Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h1 className="text-3xl lg:text-5xl font-bold mb-3">
            Game <span className="gradient-text">Platforms</span>
          </h1>
          <p className="text-sm lg:text-base text-primary-light/60 max-w-2xl mx-auto">
            Browse and find games across your favorite systems. Select a platform to view compatible games, sizes, prices, and features.
          </p>
        </motion.div>

        {/* Platform Selection Tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 3xl:grid-cols-9 gap-4 mb-10">
          {PLATFORMS_CONFIG.map((platform) => {
            const Icon = platform.icon;
            const isSelected = selectedPlatform === platform.id;
            return (
              <motion.button
                key={platform.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setSelectedPlatform(platform.id);
                  setSearchTerm("");
                }}
                className={`flex flex-col items-center justify-center p-3 sm:p-4 rounded-xl border backdrop-blur-xl transition-all duration-300 ${isSelected
                  ? `bg-gradient-to-br ${platform.gradient} text-white border-transparent shadow-lg ${platform.glowClass}`
                  : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10 hover:text-white"
                  }`}
              >
                <Icon className={`w-5 h-5 sm:w-8 sm:h-8 mb-2 ${isSelected ? "text-white" : platform.colorClass}`} />
                <span className="text-xs font-semibold text-center leading-tight">
                  {platform.name}
                </span>
              </motion.button>
            );
          })}
        </div>

        {/* Filters and Controls */}
        <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 mb-8 border border-white/10 flex flex-col md:flex-row md:justify-between md:items-center gap-4 relative z-30">
          <div className="relative w-full md:w-72">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60 w-5 h-5" />
            <input
              type="text"
              placeholder="Search platform games..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all text-sm"
            />
          </div>

          <div className="flex items-center gap-4 w-full md:w-auto justify-end">
            {/* View Controls */}
            <div className="flex items-center gap-2">
              <button
                className={`p-2 rounded-lg transition text-xl ${grid === 3
                  ? "bg-purple-600 text-white"
                  : "bg-white/10 text-gray-300 hover:bg-purple-600 hover:text-white"
                  }`}
                onClick={() => setGrid(3)}
              >
                <BsGrid3X3Gap />
              </button>
              <button
                className={`p-2 rounded-lg transition text-xl ${grid === 1
                  ? "bg-purple-600 text-white"
                  : "bg-white/10 text-gray-300 hover:bg-purple-600 hover:text-white"
                  }`}
                onClick={() => setGrid(1)}
              >
                <BsListUl />
              </button>
            </div>

            {/* Sort Control */}
            <div className="relative" ref={sortDropdownRef}>
              <button
                onClick={() => setIsSortOpen(!isSortOpen)}
                className="bg-white/10 border border-white/20 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-400 backdrop-blur flex items-center justify-between gap-2 text-sm hover:bg-white/15 transition"
              >
                {sortOptions.find((o) => o.value === sortBy)?.label}
                <svg
                  className={`w-4 h-4 transition-transform ${isSortOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isSortOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-black/90 backdrop-blur border border-white/10 rounded-lg shadow-2xl text-white z-50 overflow-hidden">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortBy(option.value);
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-white/10 text-sm transition-colors ${sortBy === option.value ? "bg-purple-500/30 text-white" : "text-white/90"
                        }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Results Counter */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-white tracking-wider">
            {PLATFORMS_CONFIG.find((p) => p.id === selectedPlatform)?.name} Games
          </h2>
          <p className="text-white/60 text-sm">
            Showing {sortedGames.length} games available on this platform
          </p>
        </div>

        {/* Games Grid/List */}
        {isLoadingGames ? (
          <ProductSkeleton grid={grid} />
        ) : sortedGames.length === 0 ? (
          <div className="text-center py-20 bg-white/5 rounded-2xl border border-white/10">
            <FiGrid className="w-16 h-16 text-white/30 mx-auto mb-4" />
            <h3 className="text-white/60 text-2xl mb-2">No games found</h3>
            <p className="text-white/40">Try adjusting your search criteria or look at another platform.</p>
          </div>
        ) : (
          <div
            className={`grid ${grid === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 3xl:grid-cols-4"
              } gap-6 xl:gap-8`}
          >
            {paginatedGames.map((game, index) => {
              const platformInfo = game.platforms?.[selectedPlatform];
              return (
                <div
                  key={game._id}
                  className={`group relative bg-white/5 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 hover:border-purple-400/50 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-purple-500/10 ${grid === 1 ? "md:flex" : ""
                    }`}
                  onMouseEnter={() => handleCardMouseEnter(game._id)}
                  onMouseLeave={() => handleCardMouseLeave(game._id)}
                  onClick={() => navigate(`/gamedetails/${game._id}`)}
                >
                  {/* Image Container */}
                  <div
                    className={`relative overflow-hidden ${grid === 1 ? "w-full md:w-64 md:h-full" : "h-52"
                      }`}
                  >
                    <img
                      src={game.cover_image?.url}
                      alt={game.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    {game.video?.url && (
                      <video
                        id={`video-${game._id}`}
                        src={game.video.url}
                        preload="none"
                        muted
                        loop
                        playsInline
                        className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>

                    {/* Action buttons */}
                    <div className="absolute top-4 right-4 flex gap-2 z-20">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleToggleCompare(game);
                        }}
                        className={`p-2 rounded-full backdrop-blur-md transition-all duration-300 ${compareGames.some((cg) => cg._id === game._id)
                          ? "bg-purple-600 text-white scale-110"
                          : "bg-black/40 text-white hover:bg-purple-600 hover:scale-110"
                          }`}
                        title="Compare Game"
                      >
                        <BiGitCompare className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleFavorite(game._id);
                        }}
                        className={`p-2 rounded-full backdrop-blur-md transition-all duration-300 ${wishlistStatus[game._id]
                          ? "bg-red-500 text-white scale-110"
                          : "bg-black/40 text-white hover:bg-red-500 hover:scale-110"
                          }`}
                      >
                        <FaHeart className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Play Hover Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <button className="bg-white/90 text-gray-900 p-4 rounded-full hover:bg-white hover:scale-110 transition-all duration-300 shadow-2xl">
                        <FiPlay className="w-6 h-6 ml-1" />
                      </button>
                    </div>
                  </div>

                  {/* Content details */}
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      <span className="text-[10px] uppercase font-bold tracking-wider text-purple-400 bg-purple-500/10 px-2.5 py-1 rounded-full mb-3 inline-block">
                        {game.category?.categoryName || "Game"}
                      </span>
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-purple-300 transition-colors line-clamp-1">
                        {game.title}
                      </h3>
                      <p className="text-gray-400 text-xs leading-relaxed mb-4 line-clamp-2">
                        {game.description}
                      </p>
                    </div>

                    <div>
                      {/* Platform Specifications */}
                      <div className="flex items-center justify-between border-t border-white/10 pt-4 mt-2">
                        <div className="text-xs text-gray-400 flex flex-col gap-1">
                          <span>Price: <strong className="text-white">${platformInfo?.price || 0}</strong></span>
                          <span>Size: <strong className="text-white">{platformInfo?.size || "N/A"}</strong></span>
                        </div>

                        {/* CTA button */}
                        {isGameInCart(game._id, selectedPlatform) ? (
                          <button
                            disabled
                            className="bg-gray-700 text-white px-3 py-1.5 text-xs rounded-lg font-medium flex items-center gap-1.5 cursor-not-allowed"
                          >
                            <FiShoppingCart className="w-3.5 h-3.5" />
                            In Cart
                          </button>
                        ) : !userId ? (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              navigate("/login");
                            }}
                            className="bg-gradient-primary text-white px-3 py-1.5 text-xs rounded-lg font-medium flex items-center gap-1.5 transition hover:scale-105"
                          >
                            <FaUser className="w-3.5 h-3.5" />
                            Login to Buy
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              dispatch(
                                addToCartAction({
                                  gameId: game._id,
                                  platform: selectedPlatform,
                                  qty: 1,
                                })
                              );
                            }}
                            className="bg-gradient-primary text-white px-3 py-1.5 text-xs rounded-lg font-medium flex items-center gap-1.5 transition hover:scale-105"
                          >
                            <FiShoppingCart className="w-3.5 h-3.5" />
                            Add to Cart
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center md:justify-end items-center mt-8 space-x-2">
            <button
              onClick={() => {
                setCurrentPage((prev) => Math.max(1, prev - 1));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              disabled={currentPage === 1}
              className="p-2 rounded-md text-white hover:bg-gray-700/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              <FaChevronLeft />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              const startPage = Math.max(
                1,
                currentPage - Math.floor(maxPageButtons / 2)
              );
              const endPage = Math.min(
                totalPages,
                startPage + maxPageButtons - 1
              );

              if (
                page === 1 ||
                page === totalPages ||
                (page >= startPage && page <= endPage)
              ) {
                return (
                  <button
                    key={page}
                    onClick={() => {
                      setCurrentPage(page);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className={`px-3 py-1.5 md:px-4 md:py-2 rounded-md text-sm sm:text-base font-medium transition-all duration-300 ring-[1px] ring-primary-light/10 ${currentPage === page
                      ? "bg-gradient-primary text-white"
                      : "text-white hover:bg-gray-700/40"
                      }`}
                  >
                    {page}
                  </button>
                );
              }
              if (
                (page === startPage - 1 && startPage > 2) ||
                (page === endPage + 1 && endPage < totalPages - 1)
              ) {
                return (
                  <span key={page} className="text-white">
                    ...
                  </span>
                );
              }
              return null;
            })}

            <button
              onClick={() => {
                setCurrentPage((prev) => Math.min(totalPages, prev + 1));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              disabled={currentPage === totalPages}
              className="p-2 rounded-md text-white hover:bg-gray-700/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              <FaChevronRight />
            </button>
          </div>
        )}
      </div>

      {/* Sticky Compare Drawer */}
      {compareGames.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[92%] max-w-5xl bg-[#0b0b0f]/95 backdrop-blur-xl border border-purple-500/40 p-4 z-40 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl shadow-[0_0_30px_rgba(147,51,234,0.3)] transition-all duration-300">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-white text-sm lg:text-base font-bold flex items-center gap-2">
              <BiGitCompare className="text-purple-400 animate-pulse text-lg" />
              Compare ({compareGames.length}/3)
            </span>
            <div className="flex gap-2 flex-wrap">
              {compareGames.map((cg) => (
                <div
                  key={cg._id}
                  className="bg-white/5 border border-white/10 px-3 py-1.5 rounded-xl flex items-center gap-2 hover:bg-white/10 transition-colors"
                >
                  <img src={cg.cover_image?.url} alt="" className="w-8 h-8 rounded-lg object-cover border border-white/10" />
                  <span className="text-white text-xs font-semibold">{cg.title}</span>
                  <button
                    onClick={() => handleToggleCompare(cg)}
                    className="text-red-400 hover:text-red-300 p-0.5 rounded-full hover:bg-white/10 transition-colors"
                  >
                    <FaTimes size={10} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4 w-full sm:w-auto justify-end">
            <button
              onClick={() => setCompareGames([])}
              className="text-white/60 hover:text-white text-xs lg:text-sm font-semibold transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={() => setIsCompareModalOpen(true)}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl text-xs lg:text-sm font-bold shadow-lg shadow-purple-500/20 hover:scale-105 hover:shadow-[0_0_15px_rgba(147,51,234,0.4)] transition-all"
            >
              Compare Now
            </button>
          </div>
        </div>
      )}

      {/* Comparison Modal */}
      <Modal
        open={isCompareModalOpen}
        onClose={() => setIsCompareModalOpen(false)}
        className="fixed inset-0 flex items-center justify-center z-[100] px-4 py-8 overflow-y-auto"
      >
        <div className="bg-[#0b0b0f]/95 backdrop-blur-2xl border border-purple-500/30 p-6 lg:p-10 text-white w-full max-w-5xl rounded-xl shadow-2xl outline-none max-h-[90vh] overflow-y-auto overflow-x-hidden scrollbar-hide relative">
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-purple-600/10 rounded-full blur-[100px] pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-blue-600/10 rounded-full blur-[100px] pointer-events-none" />

          <div className="flex justify-between items-center mb-8 pb-4 border-b border-white/10">
            <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <BiGitCompare className="text-purple-400" />
              Game Comparison
            </h2>
            <button
              onClick={() => setIsCompareModalOpen(false)}
              className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-red-500/20 hover:text-red-500 transition-colors"
            >
              <FaTimes size={16} />
            </button>
          </div>

          <div className="w-full overflow-x-auto no-scrollbar pb-2">
            <div className="min-w-[850px] md:min-w-0 grid grid-cols-1 md:grid-cols-4 gap-4 border border-white/10 rounded-2xl overflow-hidden divide-y md:divide-y-0 md:divide-x divide-white/10 bg-white/[0.02]">
              {/* Criteria Column */}
              <div className="p-4 space-y-8 font-bold text-purple-300/80 text-xs uppercase tracking-wider hidden md:block">
                <div className="h-64 flex items-end pb-2 border-b border-white/5">Game</div>
                <div className="h-12 flex items-center">Price</div>
                <div className="h-12 flex items-center">Platform Support</div>
                <div className="h-12 flex items-center">Rating</div>
                <div className="h-12 flex items-center">Download Size</div>
                <div className="h-32 flex items-center">Windows Requirements</div>
                <div className="h-32 flex items-center">Mobile & Console Specs</div>
              </div>

              {/* Game Columns */}
              {compareGames.map((cg) => {
                const rating = cg.ratings?.length
                  ? (cg.ratings.reduce((acc, r) => acc + r.rating, 0) / cg.ratings.length).toFixed(1)
                  : "No ratings";

                const winPlat = cg.platforms?.windows;
                const iosPlat = cg.platforms?.ios;
                const androidPlat = cg.platforms?.android;
                
                // Find first available platform to add to cart
                const firstAvailPlatform = Object.keys(cg.platforms || {}).find(k => cg.platforms[k]?.available);

                return (
                  <div key={cg._id} className="p-4 space-y-8 text-sm text-white flex flex-col md:block">
                    {/* Game Details Header */}
                    <div className="md:h-64 flex flex-col items-center md:items-start justify-between gap-4 pb-4 border-b border-white/5">
                      <div className="flex flex-col items-center md:items-start gap-3 w-full">
                        <img src={cg.cover_image?.url} alt="" className="w-28 h-32 rounded-xl object-cover shadow-lg border border-white/10 group-hover:scale-105 transition-transform" />
                        <div className="text-center md:text-left min-w-0 w-full">
                          <h3 className="font-bold text-base line-clamp-1 text-white hover:text-purple-300 transition-colors" title={cg.title}>{cg.title}</h3>
                          <span className="text-[10px] uppercase font-semibold text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded-full mt-1 inline-block">
                            {cg.category?.categoryName || "Game"}
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions Row */}
                      <div className="w-full flex flex-col gap-2 mt-auto">
                        <button
                          onClick={() => {
                            setIsCompareModalOpen(false);
                            navigate(`/gamedetails/${cg._id}`);
                          }}
                          className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium py-1.5 px-3 rounded-lg text-xs transition"
                        >
                          View Details
                        </button>
                        
                        {firstAvailPlatform ? (
                          isGameInCart(cg._id, firstAvailPlatform) ? (
                            <button
                              disabled
                              className="w-full bg-gray-500/20 text-gray-400 font-medium py-1.5 px-3 rounded-lg text-xs flex items-center justify-center gap-1.5 cursor-not-allowed border border-white/5"
                            >
                              <FiShoppingCart className="w-3.5 h-3.5" />
                              Already in Cart
                            </button>
                          ) : !userId ? (
                            <button
                              onClick={() => {
                                setIsCompareModalOpen(false);
                                navigate("/login");
                              }}
                              className="w-full bg-gradient-primary text-white font-medium py-1.5 px-3 rounded-lg text-xs flex items-center justify-center gap-1.5 transition hover:opacity-90"
                            >
                              <FaUser className="w-3.5 h-3.5" />
                              Login to Add
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                dispatch(
                                  addToCartAction({
                                    gameId: cg._id,
                                    platform: firstAvailPlatform,
                                    qty: 1,
                                  })
                                );
                              }}
                              className="w-full bg-gradient-primary text-white font-medium py-1.5 px-3 rounded-lg text-xs flex items-center justify-center gap-1.5 transition hover:opacity-90"
                            >
                              <FiShoppingCart className="w-3.5 h-3.5" />
                              Add to Cart
                            </button>
                          )
                        ) : (
                          <button
                            disabled
                            className="w-full bg-white/5 text-gray-500 font-medium py-1.5 px-3 rounded-lg text-xs cursor-not-allowed border border-white/5"
                          >
                            Unavailable
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Price */}
                    <div className="h-12 flex items-center justify-between md:justify-start">
                      <span className="md:hidden font-bold text-purple-300/80 mr-2 text-xs uppercase tracking-wider">Price:</span>
                      <span className="font-bold text-green-400 text-base">
                        {(() => {
                          const priceVal = cg.platforms?.[selectedPlatform]?.price;
                          if (priceVal !== undefined) return `$${priceVal}`;
                          const prices = Object.values(cg.platforms || {})
                            .filter(p => p.available)
                            .map(p => p.price);
                          return prices.length > 0 ? `$${Math.min(...prices)}` : "Free";
                        })()}
                      </span>
                    </div>

                    {/* Platform Support */}
                    <div className="h-12 flex items-center justify-between md:justify-start gap-3">
                      <span className="md:hidden font-bold text-purple-300/80 mr-2 text-xs uppercase tracking-wider">Platforms:</span>
                      <div className="flex gap-2 text-white/60">
                        {winPlat?.available && <FaWindows className="text-blue-500 text-base" title="Windows" />}
                        {iosPlat?.available && <FaApple className="text-white text-base" title="iOS" />}
                        {androidPlat?.available && <DiAndroid className="text-green-500 text-base" title="Android" />}
                        {cg.platforms?.nintendo?.available && <SiNintendoswitch className="text-red-500 text-base" title="Nintendo Switch" />}
                        {cg.platforms?.quest?.available && <SiOculus className="text-sky-400 text-base" title="Meta Quest" />}
                        {cg.platforms?.playstation?.available && <SiPlaystation className="text-blue-500 text-base" title="PlayStation" />}
                        {cg.platforms?.xbox?.available && <FaXbox className="text-green-500 text-base" title="Xbox" />}
                        {cg.platforms?.visionpro?.available && <TbDeviceVisionProFilled className="text-purple-400 text-base" title="Vision Pro" />}
                        {cg.platforms?.steamdeck?.available && <SiSteamdeck className="text-blue-400 text-base" title="Steam Deck" />}
                      </div>
                    </div>

                    {/* Rating */}
                    <div className="h-12 flex items-center justify-between md:justify-start">
                      <span className="md:hidden font-bold text-purple-300/80 mr-2 text-xs uppercase tracking-wider">Rating:</span>
                      <span className="flex items-center gap-1 text-yellow-400 font-bold text-base">
                        <FiStar className="fill-current" />
                        {rating}
                      </span>
                    </div>

                    {/* Size */}
                    <div className="h-12 flex items-center justify-between md:justify-start">
                      <span className="md:hidden font-bold text-purple-300/80 mr-2 text-xs uppercase tracking-wider">Size:</span>
                      <span className="font-medium">
                        {winPlat?.size || androidPlat?.size || iosPlat?.size || 
                         cg.platforms?.nintendo?.size || cg.platforms?.quest?.size || 
                         cg.platforms?.playstation?.size || cg.platforms?.xbox?.size || 
                         cg.platforms?.visionpro?.size || cg.platforms?.steamdeck?.size || "N/A"}
                      </span>
                    </div>

                    {/* Windows Specs */}
                    <div className="h-32 flex flex-col justify-center text-xs text-gray-300 space-y-1.5">
                      <span className="md:hidden font-bold text-purple-300/80 mb-1 text-xs uppercase tracking-wider">Windows Specs:</span>
                      {winPlat?.available ? (
                        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-2.5 space-y-1">
                          <div className="line-clamp-1"><b>OS:</b> {winPlat.system_requirements?.os || "N/A"}</div>
                          <div className="line-clamp-1"><b>CPU:</b> {winPlat.system_requirements?.processor || "N/A"}</div>
                          <div className="line-clamp-1"><b>RAM:</b> {winPlat.system_requirements?.memory || "N/A"}</div>
                          <div className="line-clamp-1"><b>GPU:</b> {winPlat.system_requirements?.graphics || "N/A"}</div>
                        </div>
                      ) : (
                        <span className="text-gray-500 italic">Not supported on Windows</span>
                      )}
                    </div>

                    {/* Mobile & Console Specs */}
                    <div className="h-32 flex flex-col justify-center text-xs text-gray-300 space-y-1.5 overflow-y-auto no-scrollbar">
                      <span className="md:hidden font-bold text-purple-300/80 mb-1 text-xs uppercase tracking-wider">Mobile & Console Specs:</span>
                      {androidPlat?.available || iosPlat?.available || cg.platforms?.nintendo?.available || cg.platforms?.quest?.available || cg.platforms?.playstation?.available || cg.platforms?.xbox?.available || cg.platforms?.visionpro?.available || cg.platforms?.steamdeck?.available ? (
                        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-2.5 space-y-1 max-h-full overflow-y-auto no-scrollbar">
                          {androidPlat?.available && (
                            <div className="line-clamp-1"><b>Android:</b> {androidPlat.system_requirements?.android_version || "N/A"}</div>
                          )}
                          {iosPlat?.available && (
                            <div className="line-clamp-1"><b>iOS:</b> {iosPlat.system_requirements?.ios_version || "N/A"}</div>
                          )}
                          {cg.platforms?.nintendo?.available && (
                            <div className="line-clamp-1"><b>Switch:</b> {cg.platforms.nintendo.system_requirements?.device_compatibility || "N/A"}</div>
                          )}
                          {cg.platforms?.quest?.available && (
                            <div className="line-clamp-1"><b>Quest:</b> {cg.platforms.quest.system_requirements?.device_compatibility || "N/A"}</div>
                          )}
                          {cg.platforms?.playstation?.available && (
                            <div className="line-clamp-1"><b>PS:</b> {cg.platforms.playstation.system_requirements?.device_compatibility || "N/A"}</div>
                          )}
                          {cg.platforms?.xbox?.available && (
                            <div className="line-clamp-1"><b>Xbox:</b> {cg.platforms.xbox.system_requirements?.device_compatibility || "N/A"}</div>
                          )}
                          {cg.platforms?.visionpro?.available && (
                            <div className="line-clamp-1"><b>Vision Pro:</b> {cg.platforms.visionpro.system_requirements?.device_compatibility || "N/A"}</div>
                          )}
                          {cg.platforms?.steamdeck?.available && (
                            <div className="line-clamp-1"><b>Steam Deck:</b> {cg.platforms.steamdeck.system_requirements?.device_compatibility || "N/A"}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500 italic">No specs available</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>
    </BackgroundColor>
    </>
  );
}